import React from 'react';
import {
  AppWindow,
  ArrowDown,
  ArrowDown01,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Asterisk,
  Banknote,
  Bell,
  Bot,
  Box,
  Brain,
  Bug,
  Building2,
  Calculator,
  ChartColumn,
  ChartColumnBig,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUpDown,
  Circle,
  CircleAlert,
  CircleCheck,
  CircleDot,
  CircleHelp,
  CirclePlus,
  CircleX,
  ClipboardList,
  Clock,
  CloudUpload,
  Code,
  Columns2,
  Compass,
  Copy,
  Database,
  Download,
  Ellipsis,
  EllipsisVertical,
  Eraser,
  ExternalLink,
  Eye,
  EyeOff,
  File,
  FileArchive,
  FileImage,
  FileText,
  Filter,
  Flag,
  FlaskConical,
  Folder,
  FolderOpen,
  Gauge,
  GitBranch,
  Globe,
  Grid3x3,
  GripVertical,
  Group,
  Hash,
  House,
  IdCard,
  Image,
  Info,
  Key,
  Landmark,
  LayoutGrid,
  Link,
  List,
  LoaderCircle,
  Lock,
  LogOut,
  Mail,
  MailOpen,
  MapPin,
  Maximize2,
  Menu,
  Minimize2,
  Monitor,
  Moon,
  Network,
  PaintRoller,
  Paintbrush,
  Palette,
  Paperclip,
  Pencil,
  Play,
  Plug,
  Plus,
  Pointer,
  QrCode,
  Redo2,
  Repeat,
  RotateCcw,
  RotateCw,
  Rows3,
  Save,
  Search,
  Send,
  Settings,
  Shapes,
  Share2,
  ShieldCheck,
  Shrink,
  SlidersHorizontal,
  Square,
  SquareChevronDown,
  SquarePen,
  Star,
  Sun,
  Table,
  Tag,
  TextCursor,
  ToggleRight,
  TrafficCone,
  Trash2,
  TriangleAlert,
  Type,
  Undo2,
  Upload,
  User,
  UserCog,
  UserRound,
  Users,
  WandSparkles,
  Wrench,
  X,
  Zap,
  ZoomOut,
  HelpCircle,
} from 'lucide-react';

/**
 * <Icon> — single replacement for the Font Awesome `<i className="fa fa-name">`
 * idiom, now backed by lucide-react. Pass the old FA name (without the `fa-`
 * prefix) and any extra className. Spin/animation: pass `spin` (or include
 * `fa-spin`/`animate-spin` in className) for the loading spinner rotation.
 *
 *   <Icon name="search" />
 *   <Icon name="spinner" spin />
 *   <Icon name="trash" className="text-danger" />
 *
 * Sizing: lucide icons default to 1em so they inherit the surrounding font
 * size exactly like FA glyphs did. Pass `size` to override (number = px).
 */
const ICONS = {
  'address-card': IdCard,
  'angle-double-left': ChevronsLeft,
  'angle-double-right': ChevronsRight,
  'angle-left': ChevronLeft,
  'angle-right': ChevronRight,
  'arrow-down': ArrowDown,
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  'arrow-up': ArrowUp,
  'asterisk': Asterisk,
  'bar-chart': ChartColumn,
  'bars': Menu,
  'bell': Bell,
  'bolt': Zap,
  'brain': Brain,
  'bug': Bug,
  'building': Building2,
  'calculator': Calculator,
  'caret-down': ChevronDown,
  'caret-left': ChevronLeft,
  'caret-right': ChevronRight,
  'caret-square-o-down': SquareChevronDown,
  'caret-up': ChevronUp,
  'check': Check,
  'check-circle': CircleCheck,
  'chevron-down': ChevronDown,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'chevron-up': ChevronUp,
  'circle': Circle,
  'circle-o-notch': LoaderCircle,
  'circle-question': CircleHelp,
  'circle-xmark': CircleX,
  'clipboard-list': ClipboardList,
  'clock': Clock,
  'clone': Copy,
  'cloud-upload': CloudUpload,
  'code': Code,
  'code-branch': GitBranch,
  'cog': Settings,
  'columns': Columns2,
  'compress': Shrink,
  'compress-arrows-alt': Minimize2,
  'copy': Copy,
  'cube': Box,
  'database': Database,
  'display': Monitor,
  'dot-circle-o': CircleDot,
  'download': Download,
  'drafting-compass': Compass,
  'edit': SquarePen,
  'ellipsis-h': Ellipsis,
  'ellipsis-v': EllipsisVertical,
  'envelope': Mail,
  'envelope-open-text': MailOpen,
  'eraser': Eraser,
  'exclamation': CircleAlert,
  'exclamation-circle': CircleAlert,
  'exclamation-triangle': TriangleAlert,
  'expand': Maximize2,
  'external-link': ExternalLink,
  'external-link-alt': ExternalLink,
  'eye': Eye,
  'eye-slash': EyeOff,
  'file': File,
  'file-alt': FileText,
  'file-archive': FileArchive,
  'file-image': FileImage,
  'file-lines': FileText,
  'file-pdf': FileText,
  'file-word': FileText,
  'filter': Filter,
  'flag-checkered': Flag,
  'flask': FlaskConical,
  'folder': Folder,
  'folder-open': FolderOpen,
  'font': Type,
  'globe': Globe,
  'grip-lines-vertical': GripVertical,
  'grip-vertical': GripVertical,
  'hand-pointer-o': Pointer,
  'hashtag': Hash,
  'home': House,
  'i-cursor': TextCursor,
  'icons': Shapes,
  'id-card-o': IdCard,
  'image': Image,
  'info-circle': Info,
  'key': Key,
  'link': Link,
  'list': List,
  'list-ul': List,
  'lock': Lock,
  'magic': WandSparkles,
  'map-marker-alt': MapPin,
  'money-bill': Banknote,
  'moon': Moon,
  'network-wired': Network,
  'object-group': Group,
  'paint-brush': Paintbrush,
  'paint-roller': PaintRoller,
  'palette': Palette,
  'paper-plane-o': Send,
  'paperclip': Paperclip,
  'pencil': Pencil,
  'play': Play,
  'plug': Plug,
  'plus': Plus,
  'plus-circle': CirclePlus,
  'poll': ChartColumnBig,
  'qrcode': QrCode,
  'question-circle': CircleHelp,
  'redo': Redo2,
  'repeat': Repeat,
  'robot': Bot,
  'rotate-left': RotateCcw,
  'rotate-right': RotateCw,
  'save': Save,
  'search': Search,
  'search-minus': ZoomOut,
  'share-alt': Share2,
  'sign-out-alt': LogOut,
  'sitemap': Network,
  'sliders': SlidersHorizontal,
  'sliders-h': SlidersHorizontal,
  'sort': ChevronsUpDown,
  'sort-down': ChevronDown,
  'sort-numeric-down': ArrowDown01,
  'sort-up': ChevronUp,
  'spinner': LoaderCircle,
  'square': Square,
  'star': Star,
  'sun': Sun,
  'table': Table,
  'tachometer-alt': Gauge,
  'tag': Tag,
  'th': Grid3x3,
  'th-large': LayoutGrid,
  'th-list': Rows3,
  'times': X,
  'times-circle': CircleX,
  'toggle-on': ToggleRight,
  'toolbox': Wrench,
  'traffic-light': TrafficCone,
  'trash': Trash2,
  'triangle-exclamation': TriangleAlert,
  'undo': Undo2,
  'university': Landmark,
  'upload': Upload,
  'user': User,
  'user-secret': UserCog,
  'user-shield': ShieldCheck,
  'user-tag': UserCog,
  'user-tie': UserRound,
  'users': Users,
  'wand-magic-sparkles': WandSparkles,
  'window-restore': AppWindow,
};

// FA modifier classes that are NOT the icon name (style/size/animation/fixed-width).
const FA_MODIFIERS = new Set([
  'fa', 'fas', 'far', 'fal', 'fab', 'fa-solid', 'fa-regular', 'fa-light', 'fa-brands',
  'fa-spin', 'fa-pulse', 'fa-fw', 'fa-fixed-width', 'fa-border', 'fa-inverse', 'fa-li',
  'fa-lg', 'fa-xs', 'fa-sm', 'fa-2x', 'fa-3x', 'fa-4x', 'fa-5x', 'fa-6x', 'fa-7x',
  'fa-8x', 'fa-9x', 'fa-10x', 'fa-rotate-90', 'fa-rotate-180', 'fa-rotate-270',
  'fa-flip-horizontal', 'fa-flip-vertical', 'fa-stack', 'fa-stack-1x', 'fa-stack-2x',
]);

type IconName = keyof typeof ICONS;
type ResolvedIcon = {
  key: IconName,
  icon: React.ComponentType,
  spin: boolean
};

/**
 * Resolve the icon name out of whatever the caller passed. Accepts:
 *   - a bare key:            "search", "caret-down"
 *   - a single fa- token:    "fa-search"
 *   - a full legacy string:  "fa fa-spinner fa-spin", "fa fa-home text-base"
 * Returns { key, spin } — the first real fa-<name> token (minus prefix) plus
 * whether a spin modifier was present. Non-fa tokens (layout classes that leak
 * in from config strings) are ignored for icon selection.
 */
function resolveIconName(name?: string): ResolvedIcon | undefined {
  const raw = String(name || '').trim();
  if (!raw) return;
  const tokens = raw.split(/\s+/);
  const spin = tokens.includes('fa-spin') || tokens.includes('fa-pulse');
  let key:  IconName | undefined = undefined;

  // Single bare token with no fa- prefix → use as-is (the common <Icon name="search"> case).
  if (tokens.length === 1 && !tokens[0].startsWith('fa-') && tokens[0] !== 'fa') {
    key = tokens[0] as IconName;
  } else {
    const iconToken = tokens.find(t => t.startsWith('fa-') && !FA_MODIFIERS.has(t));
    if (iconToken) {
      key = iconToken.slice(3) as IconName;
    }
  }

  const icon = key ? ICONS[key] : undefined;

  return icon && key ? { key, icon, spin } : undefined;
}

// Names the shim can resolve to a real Lucide glyph — used by icon pickers
// (e.g. SelectImageField) so users only ever pick icons that actually render.
export const ICON_NAMES = Object.keys(ICONS);

export interface IconProps {
  name?: string;
  /** Font Awesome class string ("Home"), or Lucide component name ("House"). */
  icon?: string | null;
  /** Pixel size forwarded to Lucide icons; used as fontSize for FA icons. */
  size?: number | string;
  /** Additional CSS classes. */
  className?: string;
  style?: React.CSSProperties;
  color?: string;
  /** Alias for size — kept for compatibility with existing callers. */
  fontSize?: number | string;
  [key: string]: unknown;
}

/**
 * Unified icon component that renders either:
 *  - A Font Awesome icon: <Icon icon="Home" />
 *  - A Lucide icon:       <House size={16} />
 *
 * Detection:
 *  - If `icon` contains spaces or matches FA/custom-icon prefixes → FA/class-based
 *  - If `icon` is a PascalCase name found in lucide-react → Lucide component
 *  - Otherwise → falls back to <i className={icon} />
 */
export function Icon({
  name,
  className = '',
  spin = false,
  size,
  strokeWidth,
  style,
  ...rest
}: IconProps) {
  const resolved = resolveIconName(name);
  const Cmp = resolved?.icon ?? HelpCircle;
  const wantSpin = spin || resolved?.spin || /\b(fa-spin|animate-spin)\b/.test(className);
  const cls = [className, wantSpin ? 'animate-spin' : '']
    .filter(Boolean)
    .join(' ')
    .trim();
  return (
    <Cmp
      className={cls || undefined}
      size={size != null ? size : '1em'}
      style={style}
      aria-hidden="true"
      {...rest}
    />
  );
}

export default Icon;
